import { EHeadingStyleVariant, ETextStyleVariant, Heading, Text } from '@outdoorsyco/bonfire';
import Image, { StaticImageData } from 'next/image';
import React from 'react';
import { useIntl } from 'react-intl';

import FastCompanyLogo from '@/assets/about/outdoorsy-group/fast-company-logo.png';
import timeline1 from '@/assets/about/outdoorsy-group/timeline-1.jpg';
import timeline2 from '@/assets/about/outdoorsy-group/timeline-2.jpg';
import timeline3 from '@/assets/about/outdoorsy-group/timeline-3.jpg';
import timeline4 from '@/assets/about/outdoorsy-group/timeline-4.jpg';
import timeline5 from '@/assets/about/outdoorsy-group/timeline-5.jpg';
import timeline6 from '@/assets/about/outdoorsy-group/timeline-6.jpg';
import timeline7 from '@/assets/about/outdoorsy-group/timeline-7.jpg';
import timeline8 from '@/assets/about/outdoorsy-group/timeline-8.jpg';
import timeline9 from '@/assets/about/outdoorsy-group/timeline-9.jpg';
import timeline10 from '@/assets/about/outdoorsy-group/timeline-10.jpg';
import timeline11 from '@/assets/about/outdoorsy-group/timeline-11.jpg';
import timeline12 from '@/assets/about/outdoorsy-group/timeline-12.jpg';
import timeline13 from '@/assets/about/outdoorsy-group/timeline-13.jpg';
import MaxWidth from '@/components/common/layout/MaxWidth';

interface IDesktopTimelineItem {
  yearText: string;
  itemText: string;
  connectorCSS: string;
  textFirst?: boolean;
  textContainerCSS?: string;
}

interface IImageGroupInfo {
  imageUrl: string | StaticImageData;
  timelineInfo: IDesktopTimelineItem;
  secondTimelineInfo?: IDesktopTimelineItem;
}

interface ITimelineItemsTypeA {
  firstImageGroupInfo: IImageGroupInfo;
  secondImageGroupInfo?: IImageGroupInfo;
}

interface ITimelineItemsTypeB {
  firstImageGroupInfo: IImageGroupInfo;
  secondImageGroupInfo: IImageGroupInfo;
  thirdImageGroupInfo: IImageGroupInfo;
  fourthImageGroupInfo: IImageGroupInfo;
  displayLogo?: boolean;
}

export const DesktopTimeline = () => {
  const intl = useIntl();

  // Type A groups
  const firstTimelineAGroup: ITimelineItemsTypeA = {
    firstImageGroupInfo: {
      imageUrl: timeline1,
      timelineInfo: {
        connectorCSS: 'w-[150px] lg:w-[250px]',
        yearText: '2014',
        itemText: intl.formatMessage({
          defaultMessage: 'Founded and incubated at NFX',
          id: 'XMPCsd',
        }),
      },
    },
    secondImageGroupInfo: {
      imageUrl: timeline2,
      timelineInfo: {
        connectorCSS: 'w-[200px] xl:w-[525px]',
        yearText: '2015',
        itemText: intl.formatMessage({ defaultMessage: 'Launch of outdoorsy.com', id: 'iwAJiT' }),
      },
    },
  };

  const secondTimelineAGroup: ITimelineItemsTypeA = {
    firstImageGroupInfo: {
      imageUrl: timeline7,
      timelineInfo: {
        connectorCSS: 'w-[150px] lg:w-[250px]',
        yearText: '2022',
        itemText: intl.formatMessage({ defaultMessage: '$2bn in sales', id: 'aCxHGd' }),
      },
    },
    secondImageGroupInfo: {
      imageUrl: timeline8,
      timelineInfo: {
        connectorCSS: 'w-[200px] xl:w-[525px]',
        yearText: '2023',
        itemText: intl.formatMessage({
          defaultMessage:
            'CEO Jeff Cavins recognized as one of the Most Exceptional Entrepreneurs of 2022 by Goldman Sachs',
          id: 'PgmGaY',
        }),
      },
    },
  };

  const thirdTimelineAGroup: ITimelineItemsTypeA = {
    firstImageGroupInfo: {
      imageUrl: timeline13,
      timelineInfo: {
        connectorCSS: 'w-[150px] lg:w-[200px]',
        yearText: '2025',
        itemText: intl.formatMessage({
          defaultMessage:
            'Appointed a Lloyd’s Coverholder, Roamly’s milestone signifies delegated underwriting authority from Lloyd’s of London, reinforcing our deep expertise in underwriting a capability crucial for mobility solutions like carsharing.',
          id: 'tryrnV',
        }),
      },
    },
  };

  // Type B groups
  const firstTimelineBGroup: ITimelineItemsTypeB = {
    firstImageGroupInfo: {
      imageUrl: timeline3,
      timelineInfo: {
        connectorCSS: 'w-[325px] lg:w-[535px]',
        yearText: '2016',
        itemText: intl.formatMessage({ defaultMessage: 'Launch of Roamly', id: 'naDx8D' }),
      },
    },
    secondImageGroupInfo: {
      imageUrl: timeline4,
      timelineInfo: {
        connectorCSS: 'w-[30px]',
        yearText: '2021',
        itemText: intl.formatMessage({ defaultMessage: '$120M Funding Round', id: 'prxEQm' }),
      },
    },
    thirdImageGroupInfo: {
      imageUrl: timeline5,
      timelineInfo: {
        connectorCSS: 'w-[300px] xl:w-[535px]',
        yearText: '2021',
        itemText: intl.formatMessage({ defaultMessage: '$1b in sales', id: 'i34fud' }),
      },
    },
    fourthImageGroupInfo: {
      imageUrl: timeline6,
      timelineInfo: {
        connectorCSS: 'w-[400px] lg:w-[535px]',
        yearText: '2022',
        itemText: intl.formatMessage({
          defaultMessage:
            'Established Outdoorsy Destination Network purchasing 3 world-class properties in CA, CO, TX',
          id: 'V1qHIv',
        }),
      },
      secondTimelineInfo: {
        connectorCSS: 'w-[400px] lg:w-[535px]',
        yearText: '2022',
        itemText: intl.formatMessage({
          defaultMessage: 'Builds first Glamping Retreat',
          id: 'v6qES+',
        }),
      },
    },
  };

  const secondTimelineBGroup: ITimelineItemsTypeB = {
    firstImageGroupInfo: {
      imageUrl: timeline9,
      timelineInfo: {
        connectorCSS: 'w-[300px] lg:w-[370px] xl:w-[400px]',
        yearText: '2023',
        itemText: intl.formatMessage({
          defaultMessage:
            "Outdoorsy named to Fast Company's prestigious World's Most Innovative Companies list in the Travel and Hospitality sector",
          id: '6Hi+XV',
        }),
        textContainerCSS: 'max-w-[150px] xxl:max-w-[250px] xxxl:max-w-[400px]',
      },
    },
    secondImageGroupInfo: {
      imageUrl: timeline10,
      timelineInfo: {
        connectorCSS: 'w-[30px]',
        yearText: '2023',
        itemText: intl.formatMessage({
          defaultMessage:
            '1,000,000th family departs on an Outdoorsy trip (7+ million nights outdoors)',
          id: 'l1aySk',
        }),
      },
    },
    thirdImageGroupInfo: {
      imageUrl: timeline11,
      timelineInfo: {
        connectorCSS: 'w-[300px] xl:w-[535px]',
        yearText: '2023',
        itemText: intl.formatMessage({
          defaultMessage: 'Outdoorsy Texas Hill Country opens',
          id: 'eC/tUT',
        }),
        textContainerCSS: 'max-w-[150px] xxxl:max-w-[400px]',
      },
    },
    fourthImageGroupInfo: {
      imageUrl: timeline12,
      timelineInfo: {
        connectorCSS: 'w-[400px] lg:w-[535px]',
        yearText: '2024',
        itemText: intl.formatMessage({
          defaultMessage:
            "Jen Young named to 'Inc.'s 2024 Female Founders 250 List' for achieving 175 percent multiyear growth",
          id: 'EhyUBW',
        }),
      },
      secondTimelineInfo: {
        connectorCSS: 'w-[400px] lg:w-[535px]',
        yearText: '2024',
        itemText: intl.formatMessage({
          defaultMessage: '$3b in sales',
          id: 'QQLDVK',
        }),
      },
    },
    displayLogo: true,
  };

  return (
    <>
      <MaxWidth className="justify-center px-6 mx-auto">
        <TimelineItemsTypeA {...firstTimelineAGroup} />

        <TimelineItemsTypeB {...firstTimelineBGroup} />

        <TimelineItemsTypeA {...secondTimelineAGroup} />

        <TimelineItemsTypeB {...secondTimelineBGroup} />

        <TimelineItemsTypeA {...thirdTimelineAGroup} />
      </MaxWidth>
    </>
  );
};

const TimelineItemsTypeA: React.FC<ITimelineItemsTypeA> = ({
  firstImageGroupInfo: firstImageInfo,
  secondImageGroupInfo: secondImageInfo,
}) => {
  return (
    <div className="mb-8">
      <div className="flex">
        <div className="relative z-[2] w-[250px] lg:w-[350px] translate-x-[50%] translate-y-[8%]">
          <Image
            className="rounded-[30px]"
            src={firstImageInfo.imageUrl}
            width={0}
            height={0}
            alt={firstImageInfo.timelineInfo.itemText}
            role="presentation"
            layout="responsive"
            objectFit="cover"
            loading="lazy"
            style={{ width: '100%', height: '100%' }}
          />
        </div>
        <div className="flex translate-x-[30%] translate-y-[50%]">
          <DesktopTimelineItem {...firstImageInfo.timelineInfo} />
        </div>
      </div>

      {secondImageInfo && (
        <div className="relative flex">
          <div className="relative z-[1] w-[250px] lg:w-[350px]">
            <Image
              className="rounded-[30px]"
              src={secondImageInfo.imageUrl}
              alt={secondImageInfo.timelineInfo.itemText}
              role="presentation"
              width={0}
              height={0}
              style={{ width: '100%', height: '100%' }}
              layout="responsive"
              objectFit="cover"
              loading="lazy"
            />
          </div>
          <div className="flex translate-y-[50%]">
            <DesktopTimelineItem {...secondImageInfo.timelineInfo} />
          </div>
        </div>
      )}
    </div>
  );
};

const TimelineItemsTypeB: React.FC<ITimelineItemsTypeB> = ({
  firstImageGroupInfo: firstImageInfo,
  secondImageGroupInfo: secondImageInfo,
  thirdImageGroupInfo: thirdImageInfo,
  fourthImageGroupInfo: fourthImageInfo,
  displayLogo,
}) => {
  return (
    <>
      <div>
        <div className="flex">
          <div className="absolute flex translate-y-[0%]">
            <DesktopTimelineItem {...firstImageInfo.timelineInfo} textFirst />
          </div>

          <div className="absolute flex translate-y-[350%] xl:translate-y-[500%]">
            <DesktopTimelineItem {...thirdImageInfo.timelineInfo} textFirst />
          </div>

          {displayLogo && (
            <div className="absolute z-[2] translate-x-[120%] translate-y-[-40%]  xl:translate-x-[150%] xl:translate-y-[-60%]">
              <div className="flex items-center justify-center rounded-[30px] w-[200px] h-[150px] lg:w-[290px] lg:h-[200px] xl:w-[290px] xl:h-[240px] bg-[#F1F2F3]">
                <Image
                  src={FastCompanyLogo.src}
                  alt="Fast Company"
                  role="presentation"
                  width={0}
                  height={0}
                  layout="responsive"
                  objectFit="cover"
                  loading="lazy"
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-end basis-[70%]">
            <div className="relative z-[1] w-[150px] lg:w-[180px] xl:w-[250px] translate-x-[15%]">
              <Image
                className="rounded-[30px]"
                src={firstImageInfo.imageUrl}
                alt={firstImageInfo.timelineInfo.itemText}
                role="presentation"
                width={0}
                height={0}
                style={{ width: '60%', height: '100%' }}
                layout="responsive"
                objectFit="cover"
                loading="lazy"
              />
            </div>
            <div className="relative z-[1] w-[200px] lg:w-[250px] xl:w-[375px]">
              <Image
                className="rounded-[30px]"
                src={secondImageInfo.imageUrl}
                alt={secondImageInfo.timelineInfo.itemText}
                role="presentation"
                width={0}
                height={0}
                style={{ width: '100%', height: '150%' }}
                layout="responsive"
                objectFit="cover"
                loading="lazy"
              />
            </div>
          </div>

          <div className="flex self-center basis-[30%] xl:translate-y-[-150%]">
            <DesktopTimelineItem {...secondImageInfo.timelineInfo} />
          </div>
        </div>

        <div className="relative flex flex-col items-end z-[2] right-[10%] translate-y-[-8%]">
          <div className="flex">
            <div className="flex translate-x-[30%] translate-y-[50%]">
              <DesktopTimelineItem {...fourthImageInfo.timelineInfo} textFirst />
            </div>
            <div className="relative z-[2] w-[250px] lg:w-[350px]">
              <Image
                className="rounded-[30px]"
                src={thirdImageInfo.imageUrl}
                alt={thirdImageInfo.timelineInfo.itemText}
                role="presentation"
                width={0}
                height={0}
                style={{ width: '100%', height: '100%' }}
                layout="responsive"
                objectFit="cover"
                loading="lazy"
              />
            </div>
          </div>

          <div className="flex">
            {fourthImageInfo.secondTimelineInfo && (
              <div className="flex translate-x-[30%] translate-y-[50%]">
                <DesktopTimelineItem {...fourthImageInfo.secondTimelineInfo} textFirst />
              </div>
            )}
            <div className="relative z-[1] w-[250px] lg:w-[350px] right-[-10%] translate-y-[-8%]">
              <Image
                className="rounded-[30px]"
                src={fourthImageInfo.imageUrl}
                alt={fourthImageInfo.timelineInfo.itemText}
                role="presentation"
                width={0}
                height={0}
                style={{ width: '100%', height: '100%' }}
                layout="responsive"
                objectFit="cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const DesktopTimelineItem: React.FC<IDesktopTimelineItem> = ({
  yearText,
  itemText,
  connectorCSS,
  textFirst,
  textContainerCSS = '',
}) => {
  if (textFirst) {
    return (
      <>
        <div className={`flex flex-col text-right ${textContainerCSS}`}>
          <Heading variant={EHeadingStyleVariant.H5}>{yearText}</Heading>
          <Text
            className={`max-w-[200px] lg:max-w-[300px] xxxl:max-w-[400px]`}
            variant={ETextStyleVariant.SmallRegular}>
            {itemText}
          </Text>
        </div>
        <div
          className={`ml-2 mt-4 h-[2px] border-white border-dotted border-t-2 ${connectorCSS}`}
        />
      </>
    );
  }
  return (
    <>
      <div className={`mr-2 mt-4 h-[2px] border-white border-dotted border-t-2 ${connectorCSS}`} />
      <div className={`flex flex-col ${textContainerCSS}`}>
        <Heading variant={EHeadingStyleVariant.H5}>{yearText}</Heading>
        <Text
          className="max-w-[200px] lg:max-w-[300px] xxxl:max-w-[400px]"
          variant={ETextStyleVariant.SmallRegular}>
          {itemText}
        </Text>
      </div>
    </>
  );
};
